// src/pages/PagoGuest.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CREATE_CHECKOUT_URL = "https://us-central1-cancionesia.cloudfunctions.net/createCheckoutSessionWeb";
const CREATE_CHECKOUT_URL_GUEST_WEB = "https://us-central1-cancionesia.cloudfunctions.net/createCheckoutSessionWeb02";

const CREATE_CHECKOUT_URL_GUEST =
  "https://us-central1-cancionesia.cloudfunctions.net/createCheckoutSessionWeb02Embedded";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PagoGuest() {
  const nav = useNavigate();
  const q = useQuery();

  const songId = (q.get("songId") || "").trim();
  const email = (q.get("email") || "").trim().toLowerCase();
  const guestId = (q.get("guestId") || "").trim();
  const currency = (q.get("currency") || "USD").trim();

  const returnUrl = useMemo(() => {
    // returnUrl debe ser HTTPS en prod
    return `${window.location.origin}/formulario?status=return&songId=${encodeURIComponent(songId)}`;
  }, [songId]);

  const payload = useMemo(
    () => ({
      uid: null,
      guestId,
      email,
      songId,
      currency,
      returnUrl,
    }),
    [guestId, email, songId, currency, returnUrl]
  );

  const ready = Boolean(songId && email && guestId);

  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Firma para pedir una sesión nueva solo cuando cambie algo relevante
  const signature = useMemo(() => {
    if (!ready) return "";
    return [guestId, email, songId, currency, returnUrl].join("|");
  }, [ready, guestId, email, songId, currency, returnUrl]);

  const lastSigRef = useRef("");

  useEffect(() => {
    if (!ready) return;

    // ya tenemos clientSecret para esta firma -> no repitas
    if (lastSigRef.current === signature && clientSecret) return;

    lastSigRef.current = signature;
    setClientSecret("");
    setErr("");
    setLoading(true);

    const controller = new AbortController();

    (async () => {
      try {
        const resp = await fetch(CREATE_CHECKOUT_URL_GUEST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || "No se pudo iniciar el pago.");
        if (!data?.clientSecret) throw new Error("Stripe clientSecret missing");

        setClientSecret(data.clientSecret);
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error(e);
        setErr(e?.message || "Error de pago");
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [ready, signature, payload, clientSecret]);

  const options = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      onComplete: () => {
        // OJO: en embedded, el return_url normalmente maneja la salida,
        // pero esto sirve para log/debug
        console.log("Checkout complete");
      },
    };
  }, [clientSecret]);

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Pago seguro</h2>
        <button className="btn" type="button" onClick={() => nav(-1)}>
          Volver
        </button>
      </div>

      <div style={{ marginTop: 12, minHeight: 620 }}>
        {!ready ? (
          <div className="note">Faltan datos para iniciar el pago (songId/email/guestId).</div>
        ) : loading ? (
          <div className="note">Cargando pago…</div>
        ) : err ? (
          <div className="alert-error" style={{ marginTop: 10 }}>
            {err}
          </div>
        ) : options ? (
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        ) : (
          <div className="note">No se pudo iniciar el pago.</div>
        )}
      </div>
    </div>
  );
}